import { CommonModule, DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LoaderService } from '../../../../core/services/loader.service';
import { Component, computed, inject, OnInit, signal, effect, untracked, ChangeDetectionStrategy } from '@angular/core';
import { ApiService } from '../../../../core/services/api.service';
import { DatePickerComponent, DateRangePickerOutput, ComparisonType, DateRangePickerValue } from '../../../../shared/components/date-picker/date-picker.component';
import { LineChartComponent } from '../../../../shared/components/charts/line-chart/line-chart.component';
import { CommonService } from '../../../../core/services/common.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { FormsModule } from '@angular/forms';
import { VISIONBOARD_CURRENCY_OPTIONS, getVisionboardCurrencyInfo } from '../../visionboard/shared/visionboard-currency.config';
import { forkJoin, catchError, of } from 'rxjs';
import { BarLineComponent, BarChartData, LineConfig, LegendData, TooltipConfig } from '../../../../shared/components/charts/bar-charts/bar-line/bar-line.component';
import { NationChartComponent } from '../../../../shared/components/charts/nation-chart/nation-chart.component';
import { GeographicMapComponent } from '../../../../shared/components/charts/geographic-map/geographic-map.component';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { MultiStackedBarChartComponent, StackedBarItem, StackedBarLegend } from '../../../../shared/components/charts/bar-charts/multi-stacked-bar-chart/multi-stacked-bar-chart.component';
import { HorizontalBarChartComponent, BarData } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { getComparisonLabel, KpiComparisonLabel } from '../../../../shared/utils/date-utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { escapeHtml } from '../../../../shared/utils/html-escape';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

export interface JourneyStage {
  key: string;
  name: string;
  value: number;
  delta_pct: number;
  pct_of_prev: number;
  sub_metrics: Record<string, number>;
}

export interface JourneyFlowRoot {
  key: string;
  name: string;
  sub_label?: string;
  value: number;
  delta_pct: number;
  sub_metrics: Record<string, number>;
}

export interface JourneyFlowResponse {
  root: JourneyFlowRoot;
  stages: JourneyStage[];
  contracts_per_business?: number;
}

export interface RecoveryFunnelStep {
  key: string;
  name: string;
  count: number;
  amount: number;
  pct_of_total: number;
  leak_count: number;
  leak_amount: number;
  leak_note: string | null;
}

@Component({
  standalone: true,
  selector: 'app-recuvery-pipeline-portfolio',
  imports: [
    CommonModule,
    FormsModule,
    DatePickerComponent,
    BarLineComponent,
    GeographicMapComponent,
    IndividualToasterComponent,
    MultiStackedBarChartComponent,
    HorizontalBarChartComponent,
  ],
  providers: [DatePipe],
  templateUrl: './recuvery-pipeline-portfolio.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './recuvery-pipeline-portfolio.component.scss'
})
export class RecuveryPipelinePortfolioComponent implements OnInit {
  private apiService = inject(ApiService);
  public _commonService = inject(CommonService);
  private loaderService = inject(LoaderService);
  private productConfigService = inject(ProductConfigService);
  private unitService = inject(UnitSuggestionService);
  private sanitizer = inject(DomSanitizer);

  isLoading = signal(false);
  isChartsLoading = signal(false);

  isAnyLoading = computed(() =>
    this.isChartsLoading() ||
    this.isGeoLoading() ||
    this.isIndustryLoading() ||
    this.isRetentionLoading() ||
    this.isTableLoading() ||
    this.isChartLoading() ||
    this.isRevenueLoading() ||
    this.isLicensingLoading()
  );

  initialLoadDone = false;

  // Date State
  datesReady = false;
  lastDateRangeKey = '';
  skipNextEffectFetch = false;
  start_date = '';
  end_date = '';
  prev_start_date = '';
  prev_end_date = '';
  comparisonType: ComparisonType = 'none';
  selectedPresetLabel = '';
  unit = signal<'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year'>('day');
  isAllTimeSelected = signal<boolean>(false);

  /**
   * KPI card delta suffix, rendered AFTER the value (e.g. "+20.0% MoM", "-99.16% vs Previous
   * Period") — reflects the selected filter's actual granularity/type ("MoM" only for a
   * calendar-month selection, "YoY" only for a calendar-year selection) instead of a hardcoded
   * "MoM". See getComparisonLabel() for the full derivation.
   */
  get comparisonLabel(): KpiComparisonLabel {
    return getComparisonLabel(this.comparisonType, this.selectedPresetLabel, this.start_date, this.end_date);
  }

  /**
   * API-facing counterpart of comparisonLabel: sent as `compare_to` on the KPI requests so the
   * backend knows which window the previous-period payload represents.
   */
  private get compareTo(): 'prevPeriod' | 'prevYear' {
    return this.comparisonType === 'previous-year' ? 'prevYear' : 'prevPeriod';
  }

  // Platform journey stage selector ("Debt Recovery" vs "Contract Lifecycle" view)
  activeJourneyTab = signal<'debt' | 'contract'>('debt');
  setJourneyTab(tab: 'debt' | 'contract'): void {
    this.activeJourneyTab.set(tab);
  }

  is90DaysSelected(): boolean {
    if (!this.start_date || !this.end_date) return false;
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    // Typical 90 days filter logic sets date range to roughly 89 or 90 days diff.
    const diff = end.diff(start, 'day');
    return diff >= 89 && diff <= 91;
  }

  currencyOptions = VISIONBOARD_CURRENCY_OPTIONS;
  selectedCurrency = signal<string>('1');
  selectedCurrencyInfo = computed(() => getVisionboardCurrencyInfo(this.selectedCurrency()));

  productConfig = computed(() => {
    const productId = this.apiService.selectedProductId();
    if (!productId) return null;
    return this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
  });

  /**
   * Endpoint prefix every visionboard request below is built on (recuvery →
   * 'visionboard'). Only the PATH lives here: the host comes from the selected
   * product's entry in environment.productServerUrls, which ApiService resolves
   * from `options.productServerName` — so test and live hosts differ without a
   * single URL being spelled out in this component.
   */
  private get toolsPath(): string {
    return this.productConfig()?.reportToolsPath || 'visionboard';
  }

  // Data signals
  enrolledBusinessesSignal = signal<any>({ value: 0, netNew: 0, avgMo: 0, mom: 0 });
  businessesWithContractSignal = signal<any>({ value: 0, toConvert: 0, goalPct: 0 });

  // Charts
  chart1Data = signal<BarChartData[]>([]);
  chart1Colors = { data1: '#5eead4' as const };
  chart1Lines: LineConfig[] = [
    { key: 'contracts', label: 'Total contracts', axis: 'right', color: '#a855f7' }
  ];
  chart1Legend: LegendData[] = [
    { label: 'Enrolled businesses', type: 'square' as any, color: '#5eead4' },
    { label: 'Total contracts', type: 'line' as any, color: '#a855f7' }
  ];
  chart1Tooltip: TooltipConfig[] = [
    { key: 'enrolled', label: 'Enrolled businesses', type: 'number' },
    { key: 'contracts', label: 'Total contracts', type: 'number' },
  ];

  // Recuvery Demo — booking status (training_statuses[], sorted by count desc)
  bookingStatusList = signal<{ name: string; count: number; pct: number; color: string }[]>([]);
  bookingStatusTotal = signal<number>(0);
  private static readonly BOOKING_STATUS_COLORS = [
    '#7C3AED', '#EC4899', '#A78BFA', '#8B5CF6', '#3B82F6', '#06B6D4', '#EF4444', '#94A3B8'
  ];


  // State mix — outreach vs. portfolio (state_outreach_vs_portfolio)
  // Full mapped states[], preserving contracts & recovered_amt for future UI use.
  stateOutreachData = signal<{ code: string; name: string; accounts: number; proposals: number; contracts: number; recovered_amt: number }[]>([]);
  stateOutreachError = signal<string>('');
  proposalConversionChartData = signal<BarData[]>([]);
  // Top 5 states shown on the chart (accounts desc, proposals tiebreaker) — the State
  // rollup list is filtered/ranked against these same states for consistency.
  stateOutreachTop5 = signal<{ code: string; name: string; accounts: number; proposals: number; contracts: number; recovered_amt: number }[]>([]);

  // State rollup (state_rollup) — accounts/paid/recovered_amt by state
  stateRollupRaw = signal<{ code: string; name: string; accounts: number; paid: number; recovered_amt: number }[]>([]);
  stateRollupError = signal<string>('');
  private static readonly STATE_ROLLUP_RANK_COLORS = [
    'text-purple-600', 'text-blue-600', 'text-emerald-600', 'text-teal-600', 'text-orange-600'
  ];
  private static readonly STATE_ROLLUP_OTHER_COLOR = 'text-slate-500 dark:text-slate-400';

  // Top 5 (matching the chart's ranking) + an "Other States" aggregate of everything else,
  // built purely from stateRollupRaw() — no invented values.
  stateRollupDisplay = computed(() => {
    const raw = this.stateRollupRaw();
    if (!raw.length) return [];

    const total = raw.reduce((sum, s) => sum + s.accounts, 0);
    const top5Keys = new Set(
      this.stateOutreachTop5().map(s => (s.code || s.name || '').toLowerCase())
    );

    const matchKey = (s: { code: string; name: string }) => (s.code || s.name || '').toLowerCase();

    const top5Rows = raw.filter(s => top5Keys.has(matchKey(s)));
    const otherRows = raw.filter(s => !top5Keys.has(matchKey(s)));

    // Preserve the chart's ranking order for the top 5.
    const orderedTop5 = this.stateOutreachTop5()
      .map(t => top5Rows.find(r => matchKey(r) === matchKey(t)))
      .filter((r): r is { code: string; name: string; accounts: number; paid: number; recovered_amt: number } => !!r);

    const rows = orderedTop5.map((s, i) => ({
      name: s.name,
      accounts: s.accounts,
      recovered_amt: s.recovered_amt,
      share: total > 0 ? (s.accounts / total) * 100 : 0,
      rate: s.accounts > 0 ? (s.paid / s.accounts) * 100 : 0,
      colorClass: RecuveryPipelinePortfolioComponent.STATE_ROLLUP_RANK_COLORS[i] || RecuveryPipelinePortfolioComponent.STATE_ROLLUP_OTHER_COLOR
    }));

    if (otherRows.length) {
      const otherAccounts = otherRows.reduce((sum, s) => sum + s.accounts, 0);
      const otherPaid = otherRows.reduce((sum, s) => sum + s.paid, 0);
      const otherRecovered = otherRows.reduce((sum, s) => sum + s.recovered_amt, 0);
      rows.push({
        name: 'Other States',
        accounts: otherAccounts,
        recovered_amt: otherRecovered,
        share: total > 0 ? (otherAccounts / total) * 100 : 0,
        rate: otherAccounts > 0 ? (otherPaid / otherAccounts) * 100 : 0,
        colorClass: RecuveryPipelinePortfolioComponent.STATE_ROLLUP_OTHER_COLOR
      });
    }

    return rows;
  });

  // Geography State — state_geo_metrics
  isGeoLoading = signal(false);
  mapDataError = signal<string>('');
  geoMetricMode = signal<'biz' | 'con'>('biz');

  // State Licensing Stats — Accounts in States Allowed / Not Allowed cards
  isLicensingLoading = signal(false);
  stateLicensingStatsError = signal<string>('');
  stateLicensingStats = signal({
    allowed_count: 0,
    allowed_states_n: 0,
    allowed_pct: 0,
    not_allowed_count: 0,
    not_allowed_states_n: 0,
    not_allowed_pct: 0
  });

  mapData = signal<any[]>([]);
  mapValueKeys = signal({ keyName: 'state', valueKey: 'businessCount' });
  currentMapTotal = signal<number>(0);

  // Deep Dive Modal
  isDeepDiveModalOpen = signal(false);
  deepDiveData = signal<any>(null);
  isDeepDiveLoading = signal(false);
  deepDiveError = signal<string>('');

  // No Leads Modal
  // isNoLeadsModalOpen = signal(false);
  // noLeadsData = signal<any>(null);

  // Industry Data
  isIndustryLoading = signal(false);
  topCategoriesData = signal<BarChartData[]>([]);
  topIndustrySummary = signal<string[]>([]);

  categoryChartColors = { data1: '#009688' as const };
  categoryChartTooltip: TooltipConfig[] = [
    { key: 'contracts', label: 'Total Contracts', type: 'number' },
  ];

  riskQuadrantData = signal<any>({ q1: [], q2: [], q3: [], q4: [] });

  // Platform journey — stage cards, backed by journey_flow_debt / journey_flow_contract
  isJourneyLoading = signal(false);
  journeyFlowDebt = signal<JourneyFlowResponse | null>(null);
  journeyFlowContract = signal<JourneyFlowResponse | null>(null);
  activeJourneyFlow = computed<JourneyFlowResponse | null>(() =>
    this.activeJourneyTab() === 'debt' ? this.journeyFlowDebt() : this.journeyFlowContract()
  );

  getJourneyStage(key: string): JourneyStage | null {
    return this.activeJourneyFlow()?.stages.find(s => s.key === key) ?? null;
  }

  // Which recovery stage currently carries the largest dollar debt exposure — compares the
  // actual API debt amounts (not account counts/percentages) so the "LARGEST DEBT EXPOSURE"
  // badge follows the real numbers instead of being pinned to one stage. Recomputes whenever
  // the underlying journey-flow signals change (date range, product, etc.), since it reads
  // through getJourneyStage()/activeJourneyFlow(). Ties are broken deterministically in favor
  // of Notification, the earlier stage in the flow.
  largestDebtExposureStage = computed<'notification' | 'collection' | null>(() => {
    const notification = this.getJourneyStage('notification');
    const collection = this.getJourneyStage('collection');

    const notificationDebt = notification ? (Number(notification.sub_metrics?.['debt_exposure']) || 0) : null;
    const collectionDebt = collection ? (Number(collection.sub_metrics?.['debt_in_collection']) || 0) : null;

    if (notificationDebt === null && collectionDebt === null) return null;
    if (notificationDebt === null) return 'collection';
    if (collectionDebt === null) return 'notification';

    return collectionDebt > notificationDebt ? 'collection' : 'notification';
  });

  // Recovery Conversion & Value Leak funnel
  isConversionFunnelLoading = signal(false);
  recoveryConversionFunnel = signal<RecoveryFunnelStep[]>([]);

  // Adds the solid/hatched bar widths, and the hover-tooltip copy, the funnel UI needs —
  // all derived from the mapped step fields only, no extra server fields required.
  recoveryFunnelRows = computed(() => {
    const steps = this.recoveryConversionFunnel();
    return steps.map((step, idx) => {
      const next = steps[idx + 1];
      const isLast = !next;
      const leak = this.parseLeakNote(step.leak_note);
      const solidWidth = isLast ? step.pct_of_total : next.pct_of_total;
      const hatchedWidth = isLast ? 0 : Math.max(step.pct_of_total - next.pct_of_total, 0);
      return {
        ...step,
        isLast,
        solidWidth,
        hatchedWidth,
        // Tooltip anchor points, clamped so the card stays inside the bar's row.
        solidTooltipLeft: Math.min(Math.max(solidWidth / 2, 8), 92),
        hatchedTooltipLeft: Math.min(Math.max(solidWidth + hatchedWidth / 2, 8), 92),
        nextLabel: next ? this.recoveryFunnelShortLabel(next.key) : '',
        nextCount: next ? next.count : 0,
        nextPct: next ? next.pct_of_total : 0,
        leakTitle: leak.title,
        leakSuffix: leak.suffix,
      };
    });
  });

  // Which bar segment (if any) currently has the mouse over it, for the hover tooltip.
  hoveredFunnelSegment = signal<{ rowKey: string; type: 'solid' | 'hatched' } | null>(null);
  setHoveredFunnelSegment(rowKey: string, type: 'solid' | 'hatched'): void {
    this.hoveredFunnelSegment.set({ rowKey, type });
  }
  clearHoveredFunnelSegment(): void {
    this.hoveredFunnelSegment.set(null);
  }

  private static readonly RECOVERY_FUNNEL_SUB_LABELS: Record<string, string> = {
    debt_assigned: 'New Account · full book',
    notice_delivered: 'demand letter sent · Recovery & Lendee only',
    responded: 'pay / plan / partial / dispute',
    recovered: 'Resolution',
  };

  private static readonly RECOVERY_FUNNEL_BAR_COLORS: Record<string, string> = {
    debt_assigned: 'bg-slate-600',
    notice_delivered: 'bg-indigo-600',
    responded: 'bg-amber-500',
    recovered: 'bg-emerald-600',
  };

  // Short form of the next stage's name for the "Advances to <label>" tooltip line.
  private static readonly RECOVERY_FUNNEL_SHORT_LABELS: Record<string, string> = {
    debt_assigned: 'debt assigned',
    notice_delivered: 'notice',
    responded: 'response',
    recovered: 'recovery',
  };

  recoveryFunnelSubLabel(key: string): string {
    return RecuveryPipelinePortfolioComponent.RECOVERY_FUNNEL_SUB_LABELS[key] || '';
  }

  recoveryFunnelBarColor(key: string): string {
    return RecuveryPipelinePortfolioComponent.RECOVERY_FUNNEL_BAR_COLORS[key] || 'bg-slate-600';
  }

  recoveryFunnelShortLabel(key: string): string {
    return RecuveryPipelinePortfolioComponent.RECOVERY_FUNNEL_SHORT_LABELS[key] || key;
  }

  // Splits a server leak_note like "not yet actioned (upload backlog)" or
  // "no response — largest leak" into a bold tooltip title and a muted caption suffix.
  private parseLeakNote(note: string | null): { title: string; suffix: string } {
    if (!note) return { title: '', suffix: '' };
    const parenMatch = note.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
    if (parenMatch) {
      return { title: this.capitalize(parenMatch[1].trim()), suffix: parenMatch[2].trim() };
    }
    const dashParts = note.split(/\s*—\s*/);
    if (dashParts.length > 1) {
      return { title: this.capitalize(dashParts[0].trim()), suffix: dashParts.slice(1).join(', ').trim() };
    }
    return { title: this.capitalize(note.trim()), suffix: '' };
  }

  private capitalize(text: string): string {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
  }

  // Retention Data
  isRetentionLoading = signal(false);
  topBusinessesList = signal<any[]>([]);
  anchorRiskData = signal<any>(null);
  totalBookContracts = signal<number>(0);
  anchorRisk = computed(() => {
    const rawData = this.anchorRiskData();
    console.log(rawData, 'rwadad');
    if (rawData && rawData.flags) {
      for (const flag of rawData.flags) {
        if (flag.category === 'verification_backlog') {

          console.log(flag.category, 'category');
          return {
            topBusinessName: flag?.category || 'verification_backlog',
            topBusinessContracts: flag?.finding_text || 0,
            topAccountsCount: flag?.metric_value || 6,
            topAccountsSharePct: flag?.metric_pct || 0,
            headline: flag?.headline || ''
          };
        }
      }
    }

    const list = this.topBusinessesList();

    console.log(list, 'list');
    if (!list || list.length === 0) {
      return {
        topBusinessName: 'Crown Distributors',
        topBusinessContracts: 0,
        topAccountsCount: 6,
        topAccountsSharePct: 38,
        headline: ''
      };
    }

    const sorted = [...list].sort((a, b) => {
      const valA = Number(a.total_contracts !== undefined ? a.total_contracts : (a.contracts !== undefined ? a.contracts : (a.contract_count !== undefined ? a.contract_count : (a.count !== undefined ? a.count : (a.value !== undefined ? a.value : 0)))));
      const valB = Number(b.total_contracts !== undefined ? b.total_contracts : (b.contracts !== undefined ? b.contracts : (b.contract_count !== undefined ? b.contract_count : (b.count !== undefined ? b.count : (b.value !== undefined ? b.value : 0)))));
      return valB - valA;
    });

    const topItem = sorted[0];
    const topBusinessName = topItem?.business_name || topItem?.business || topItem?.name || topItem?.company_name || 'Crown Distributors';
    const topBusinessContracts = Number(topItem?.total_contracts !== undefined ? topItem?.total_contracts : (topItem?.contracts !== undefined ? topItem?.contracts : (topItem?.contract_count !== undefined ? topItem?.contract_count : (topItem?.count !== undefined ? topItem?.count : (topItem?.value !== undefined ? topItem?.value : 0)))));

    const topN = Math.min(6, sorted.length);
    const topAccountsCount = topN;
    const topNContractsSum = sorted.slice(0, topN).reduce((sum, item) => {
      const val = Number(item.total_contracts !== undefined ? item.total_contracts : (item.contracts !== undefined ? item.contracts : (item.contract_count !== undefined ? item.contract_count : (item.count !== undefined ? item.count : (item.value !== undefined ? item.value : 0)))));
      return sum + val;
    }, 0);

    let totalContracts = this.totalBookContracts();
    if (!totalContracts || totalContracts < topNContractsSum) {
      const sumAll = sorted.reduce((sum, item) => {
        const val = Number(item.total_contracts !== undefined ? item.total_contracts : (item.contracts !== undefined ? item.contracts : (item.contract_count !== undefined ? item.contract_count : (item.count !== undefined ? item.count : (item.value !== undefined ? item.value : 0)))));
        return sum + val;
      }, 0);
      totalContracts = Math.max(totalContracts, sumAll);
    }

    const topAccountsSharePct = totalContracts > 0 ? Math.round((topNContractsSum / totalContracts) * 100) : 38;

    return {
      topBusinessName: topBusinessName || 'Crown Distributors',
      topBusinessContracts: topBusinessContracts || 0,
      topAccountsCount: topAccountsCount || 6,
      topAccountsSharePct: Math.min(100, Math.max(0, topAccountsSharePct)),
      headline: ''
    };
  });

  disengagedAccounts = signal({
    dndCount: 842,
    dndPct: 11.1
  });
  // Table & Chart Data
  isTableLoading = signal(false);
  contractStateSummary = signal<any[]>([]);
  contractStateSummaryError = signal<string>('');
  contractStateHeadline = signal<string>('Verification, payment & dispute breakdown');
  contractTypesCountData = signal<any>({ total: 0, highestCount: 0, highestLabel: '', highestPercentage: 0 });

  isChartLoading = signal(false);
  contractStatusTrends = signal<StackedBarItem[]>([]);
  contractStatusTrendsError = signal<string>('');
  contractStatusCounts = signal({ month: '', active: 0, overdue: 0, completed: 0, cancelled: 0 });

  isRevenueLoading = signal(true);
  allowedGroupByUnits: any[] = ['day', 'week', 'month', 'quarter', 'year'];

  statusChartLegend: StackedBarLegend[] = [
    { key: 'active', label: 'Active', color: '#8A2BE2' },
    { key: 'overdue', label: 'Overdue', color: '#D97706' },
    { key: 'completed', label: 'Completed', color: '#059669' },
    { key: 'cancelled', label: 'Cancelled', color: '#dc2626' }
  ];

  private getPayload() {
    const productId = this.apiService.selectedProductId();
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId!)
    );
    const payload: any = {
      start_date: this.start_date,
      end_date: this.end_date,
      unit: this.unit(),
      overall: 1,
      // is_dummy: 0,
      cocu_id: this.selectedCurrencyInfo().cocu_id,
      country_id: this.selectedCurrencyInfo().country_id,
      ...productConfig.payloadExtras
    };
    if (productConfig.productServerName === 'credee') {
      payload.timezone = 'PDT';
    }
    return payload;
  }

  private getPayloadData() {
    const productId = this.apiService.selectedProductId();
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId!)
    );
    const payload: any = {
      start_time: this.start_date,
      end_time: this.end_date,
      unit: this.unit(),
      country_id: this.selectedCurrencyInfo().country_id,
      ...productConfig.payloadExtras
    };
    if (productConfig.productServerName === 'credee') {
      payload.timezone = 'PDT';
    }
    return payload;
  }
  private getPreviousPayload() {
    if (this.comparisonType !== 'none' && this.prev_start_date && this.prev_end_date) {
      const payload = this.getPayload();
      payload.start_date = this.prev_start_date;
      payload.end_date = this.prev_end_date;
      return payload;
    }
    return null;
  }

  constructor() {
    effect(() => {
      if (this.isAnyLoading()) {
        if (!this.initialLoadDone) {
          untracked(() => this.loaderService.show());
        }
      } else {
        untracked(() => {
          this.loaderService.hide();
          this.initialLoadDone = true;
        });
      }
    });

    effect(() => {
      const pid = this.apiService.selectedProductId();
      if (pid && this.datesReady) {
        untracked(() => {
          if (this.skipNextEffectFetch) {
            this.skipNextEffectFetch = false;
            return;
          }
          this.fetchData();
        });
      }
    });
  }

  ngOnInit() {
    // Initial fetch driven by date picker
  }

  onDateRangeChange(event: DateRangePickerOutput) {
    const rangeKey = [
      event.start_date,
      event.end_date,
      event.comparison_type,
      event.previous_start_date ?? '',
      event.previous_end_date ?? '',
      event.unit
    ].join('|');

    if (rangeKey === this.lastDateRangeKey) return;
    this.lastDateRangeKey = rangeKey;

    this.start_date = this.normalizeDate(event.start_date);
    this.end_date = this.normalizeDate(event.end_date);
    this.comparisonType = event.comparison_type;
    this.selectedPresetLabel = event.preset_label || '';
    this.isAllTimeSelected.set(event.preset_label === 'All Time' || dayjs(event.end_date).diff(dayjs(event.start_date), 'year') >= 5);

    if (event.comparison_type !== 'none' && event.previous_start_date && event.previous_end_date) {
      this.prev_start_date = this.normalizeDate(event.previous_start_date);
      this.prev_end_date = this.normalizeDate(event.previous_end_date);
    } else {
      this.prev_start_date = '';
      this.prev_end_date = '';
    }

    if (event.preset_label === 'Today' || event.preset_label === 'Yesterday' || this.start_date === this.end_date) {
      this.allowedGroupByUnits = ['hour', 'day', 'week', 'month', 'quarter', 'year'];
    } else {
      this.allowedGroupByUnits = ['day', 'week', 'month', 'quarter', 'year'];
    }

    this.unit.set(event.unit || this.unitService.getUnitSuggestions(this.start_date, this.end_date).recommended as any);
    this.datesReady = true;
    this.skipNextEffectFetch = true;
    this.fetchData();
  }

  rangeValues(_event: DateRangePickerValue) { }

  onCurrencyChange(value: string) {
    if (value === this.selectedCurrency()) return;
    this.selectedCurrency.set(value);
    if (this.datesReady) {
      this.fetchData();
    }
  }

  private normalizeDate(date: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return dayjs.tz(date, LA_TIMEZONE).format('YYYY-MM-DD');
    }
    return dayjs(date).tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  isOneYearOrMore(): boolean {
    if (!this.start_date || !this.end_date) return false;
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    return end.diff(start, 'day') >= 364;
  }

  get formattedDateRange(): string {
    if (!this.start_date || !this.end_date) return '';
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    return `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`;
  }

  shouldEnableScroll(): boolean {
    if (!this.start_date || !this.end_date) return false;
    const start = dayjs(this.start_date);
    const end = dayjs(this.end_date);
    const diffYears = end.diff(start, 'year');
    const diffDays = end.diff(start, 'day');

    if (diffYears >= 3) return true;
    if (diffDays >= 29 && this.unit() === 'week') return true;
    if (diffDays >= 29 && this.unit() === 'day') return true;

    return false;
  }

  getChartMinWidth(dataLength: number): number | null {
    if (dataLength >= 12 || this.isOneYearOrMore() || this.shouldEnableScroll()) {
      if (dataLength < 8) {
        return 1200;
      } else if (dataLength < 15) {
        // Increase base scroll for smaller lengths like 90 days grouped by week (~13 weeks)
        return 2400;
      }
      // Increase width per item to ensure a substantial scroll
      return dataLength * 200;
    }
    return null;
  }

  getAllTimeChartMinWidth(dataLength: number): number | null {
    if (this.isAllTimeSelected() && dataLength > 0) {
      return Math.max(1200, dataLength * 100);
    }
    return null;
  }

  fetchData() {
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.datesReady) return;

    this.isLoading.set(true);

    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );

    const options = {
      showErrorToaster: true,
      showSuccessToaster: false,
      showLoader: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };

    const payload = this.getPayload();
    const prevPayload = this.getPreviousPayload();

    this.isChartsLoading.set(true);
    // Clear immediately so a stale chart from the previous date range/unit never lingers
    // while the new request is in flight.
    this.chart1Data.set([]);

    const toolPath = this.toolsPath;
    const listPayload = { ...payload, page: 1, limit: 1 };
    delete listPayload.cocu_id;
    delete listPayload.productId;
    delete listPayload.country_id;
    delete listPayload.overall;
    delete listPayload.timezone;

    let txListPayloadPrev: any = null;
    let implicitPrevPayload: any = null;
    if (prevPayload) {
      implicitPrevPayload = { ...prevPayload };
      txListPayloadPrev = { ...prevPayload, page: 1, limit: 1 };
    } else {
      const start = dayjs(this.start_date);
      const end = dayjs(this.end_date);
      const diffDays = end.diff(start, 'day');
      const prevStart = start.subtract(diffDays + 1, 'day').format('YYYY-MM-DD');
      const prevEnd = start.subtract(1, 'day').format('YYYY-MM-DD');

      implicitPrevPayload = { ...payload, start_time: prevStart, end_time: prevEnd };
      txListPayloadPrev = { ...implicitPrevPayload, page: 1, limit: 1 };
    }

    delete txListPayloadPrev.cocu_id;
    delete txListPayloadPrev.productId;
    delete txListPayloadPrev.country_id;
    delete txListPayloadPrev.overall;
    delete txListPayloadPrev.timezone;

    // Business Adoption & Contract Pipeline KPIs (Businesses Enrolled / Businesses With Contract)
    const overallPayload: any = {
      ...this.getPayloadData(),
      overall: 1,
      productId,
    };
    if (implicitPrevPayload) {
      overallPayload.compare_to = this.compareTo;
    }

    const outreachPayload = { ...this.getPayloadData(), productId };

    const requests: any = {
      proposalConversion: this.apiService.post(`${toolPath}/state_outreach_vs_portfolio`, outreachPayload, options).pipe(catchError(() => of(null))),
      overall: this.apiService.post(`${toolPath}/business_contracts_overall`, overallPayload, options).pipe(catchError(() => of(null))),
      enrolledRunningTotal: this.apiService.post(`${toolPath}/business_enrolled_running_total`, overallPayload, options).pipe(catchError(() => of(null))),
      contractsRunningTotal: this.apiService.post(`${toolPath}/contracts_created_running_total`, overallPayload, options).pipe(catchError(() => of(null))),
      bookingStatus: this.apiService.post(`${toolPath}/business_training_stats`, overallPayload, options).pipe(catchError(() => of(null))),
    };

    let prevTimePayload: any = null;
    if (implicitPrevPayload) {
      prevTimePayload = {
        ...implicitPrevPayload,
        start_time: implicitPrevPayload.start_time || implicitPrevPayload.start_date,
        end_time: implicitPrevPayload.end_time || implicitPrevPayload.end_date,
        productId,
        compare_to: this.compareTo,
      };
      delete prevTimePayload.start_date;
      delete prevTimePayload.end_date;

      requests['overallPrev'] = this.apiService.post(`${toolPath}/business_contracts_overall`, prevTimePayload, options).pipe(catchError(() => of(null)));
    }
    forkJoin(requests).subscribe({
      next: (results: any) => {
        this.processProposalConversionData(results.proposalConversion);
        this.processOverallData(results.overall?.data, results.overallPrev?.data, results.enrolledRunningTotal?.data || []);
        this.processRunningTotals(results.enrolledRunningTotal?.data || [], results.contractsRunningTotal?.data || []);
        this.processBookingStatuses(results.bookingStatus?.data);

        this.isChartsLoading.set(false);
      },
      error: () => {
        this.isChartsLoading.set(false);
      }
    });

    this.fetchMapData();
    this.fetchStateLicensingStats();
    this.fetchIndustryData();
    this.fetchRetentionData();
    this.fetchJourneyFlows();
    this.fetchRecoveryConversionFunnel();
    this.fetchContractStatusTrends();
    this.fetchRevenueStreamMix();
    this.fetchContractStateSummary();
  }

  // GEOGRAPHY DATA FETCHING
  setGeoMetricMode(mode: 'biz' | 'con') {
    this.geoMetricMode.set(mode);
    this.fetchMapData();
  }

  // API: { states: [{ code, businesses, contracts }] } — "leads" is hidden for now.
  private fetchMapData() {
    this.isGeoLoading.set(true);
    this.mapDataError.set('');
    const productId = this.apiService.selectedProductId();
    const metricMode = this.geoMetricMode();
    const payload = { ...this.getPayloadData(), metric: metricMode, productId };
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'recuvery' };
    const toolPath = `${this.toolsPath}/state_geo_metrics`;

    this.apiService.post(toolPath, payload, options)
      .pipe(catchError(() => of(null)))
      .subscribe({
        next: (res: any) => {
          if (!res) {
            this.mapData.set([]);
            this.currentMapTotal.set(0);
            this.mapDataError.set('Unable to load geographic data');
            this.isGeoLoading.set(false);
            return;
          }
          this.processStateMapData(res);
          this.isGeoLoading.set(false);
        },
        error: () => {
          this.mapData.set([]);
          this.currentMapTotal.set(0);
          this.mapDataError.set('Unable to load geographic data');
          this.isGeoLoading.set(false);
        }
      });
  }

  private processStateMapData(res: any) {
    const raw = res?.data ?? res;
    const states: any[] = Array.isArray(raw?.states) ? raw.states : [];
    const metricMode = this.geoMetricMode();
    const valueKey = metricMode === 'biz' ? 'businesses' : 'contracts';

    // Only the selected metric's field is included per state (not both), so the map's
    // tooltip — which reads whichever fields are present on the record — shows just that
    // one metric, and eligibility (value > 0) is evaluated against it alone.
    let total = 0;
    const mapped: any[] = states
      .filter((s: any) => s.code)
      .map((s: any) => {
        const value = Number(valueKey === 'businesses' ? s.businesses : s.contracts) || 0;
        total += value;
        return {
          state: s.code,
          country: 'United States',
          country_code: 'US',
          [valueKey]: value
        };
      });

    this.mapData.set(mapped);
    this.currentMapTotal.set(total);
    this.mapValueKeys.set({ keyName: 'state', valueKey });
  }

  onRegionClick(event: any) {
    const regionName = event.feature?.properties?.name || 'Unknown Region';
    const countryCode = event.feature?.properties?._countryCode || 'US';
    const countryLabel = countryCode === 'US' ? 'United States' : (countryCode === 'GB' ? 'United Kingdom' : (countryCode === 'AU' ? 'Australia' : countryCode));
    const metricMode = this.geoMetricMode();
    const isBizMode = metricMode === 'biz';
    const productId = this.apiService.selectedProductId();

    const payload: any = {
      ...this.getPayloadData(),
      productId,
      state: regionName.toLowerCase(),
    };

    this.isDeepDiveModalOpen.set(true);
    this.isDeepDiveLoading.set(true);
    this.deepDiveError.set('');
    this.deepDiveData.set(null);
    document.body.style.overflow = 'hidden';

    const toolPath = `${this.toolsPath}/state_market_detail`;
    this.apiService.post(toolPath, payload, { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'recuvery' })
      .pipe(catchError((err) => of({ __error: err })))
      .subscribe({
        next: (res: any) => {
          if (!res || res.__error) {
            console.error('[state_market_detail] request failed', { regionName, payload, error: res?.__error });
            this.deepDiveError.set('Unable to load market details for this region. Please try again.');
            this.isDeepDiveLoading.set(false);
            return;
          }

          // Confirmed response envelope (verified against the live endpoint for multiple
          // states): { is_error, data: { state, businesses, contracts, accounts,
          // new_last_30_days, new_last_7_days, avg_contracts_per_business,
          // trend_totals: { businesses, contracts, peak_businesses_per_day,
          // peak_contracts_per_day, avg_businesses_per_day, avg_contracts_per_day },
          // daily_trend: [{ date, businesses, contracts }], top_businesses: [{ name,
          // category, industry, accounts, contracts, financed_amt }] } }. There is no
          // "kpis" wrapper and no separate "active businesses" field — `businesses` is
          // the state's business count for the selected range, used for both.
          const data = res?.data ?? {};

          if (res.is_error || !data || typeof data !== 'object' || Object.keys(data).length === 0) {
            console.error('[state_market_detail] empty/error response body', { regionName, payload, res });
            this.deepDiveError.set('No market data returned for this region.');
            this.isDeepDiveLoading.set(false);
            return;
          }

          const num = (v: any): number => {
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
          };

          const trendTotals = data.trend_totals ?? {};
          const dailyTrend: any[] = Array.isArray(data.daily_trend) ? data.daily_trend : [];

          const totalBiz = num(data.businesses);
          const totalCon = num(data.contracts);
          const activeBiz = totalBiz;
          const avgContracts = num(data.avg_contracts_per_business);

          // "Last 30/7 days" reflect the selected metric mode: new businesses onboarded
          // for Businesses mode (API-provided), contracts created for Contracts mode
          // (summed from the day-level trend, since the API only totals contracts over
          // the whole trend window, not a separate 7-day figure).
          const last30 = isBizMode ? num(data.new_last_30_days) : num(trendTotals.contracts);
          const last7 = isBizMode
            ? num(data.new_last_7_days)
            : dailyTrend.slice(-7).reduce((sum, d) => sum + num(d?.contracts), 0);
          const peak = isBizMode ? num(trendTotals.peak_businesses_per_day) : num(trendTotals.peak_contracts_per_day);

          let sparklineData = dailyTrend.map((d: any) => isBizMode ? num(d?.businesses) : num(d?.contracts));
          if (sparklineData.length === 0) sparklineData = Array.from({ length: 30 }, () => 0);

          const svgWidth = 300;
          const svgHeight = 64;
          const xStep = sparklineData.length > 1 ? svgWidth / (sparklineData.length - 1) : svgWidth;
          const sparklinePath = sparklineData.map((val: number, i: number) => {
            const x = i * xStep;
            const y = peak > 0 ? svgHeight - (val / peak * svgHeight * 0.9) : svgHeight;
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y.toFixed(1)}`;
          }).join(' ');
          const sparklineArea = `${sparklinePath} L ${svgWidth},${svgHeight} L 0,${svgHeight} Z`;

          const topBusinessesRaw: any[] = Array.isArray(data.top_businesses) ? data.top_businesses : [];
          const topBusinesses = topBusinessesRaw.map((b: any) => ({
            businessName: b?.name || 'Unknown',
            category: b?.category || '',
            industry: b?.industry || '',
            accounts: num(b?.accounts),
            total: num(b?.contracts),
            financed_amount: num(b?.financed_amt)
          }));

          // No top-level "top 8 financed" figure is returned — top_businesses is already
          // ranked by contract volume descending, so this sums the financed amount of the
          // top 8 entries, matching the "TOP-8 FINANCED" label.
          const financedTotal = topBusinesses.slice(0, 8).reduce((sum, b) => sum + b.financed_amount, 0);

          const segLabel = 'All industries';

          this.deepDiveData.set({
            regionName,
            countryLabel,
            isBizMode,
            segLabel,
            totalBiz,
            activeBiz,
            totalCon,
            last30,
            last7,
            avgContracts,
            peak,
            sparklineData,
            sparklinePath,
            sparklineArea,
            topBusinesses,
            financedTotal
          });

          this.isDeepDiveLoading.set(false);
        },
        error: (err) => {
          console.error('[state_market_detail] subscribe error', { regionName, payload, err });
          this.deepDiveError.set('Unable to load market details for this region. Please try again.');
          this.isDeepDiveLoading.set(false);
        }
      });
  }

  closeDeepDiveModal() {
    this.isDeepDiveModalOpen.set(false);
    this.deepDiveData.set(null);
    this.deepDiveError.set('');
    document.body.style.overflow = '';
  }

  // STATE LICENSING STATS — Accounts in States Allowed / Not Allowed cards
  private fetchStateLicensingStats() {
    this.isLicensingLoading.set(true);
    this.stateLicensingStatsError.set('');
    const productId = this.apiService.selectedProductId();
    const payload = { ...this.getPayloadData(), productId };
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'recuvery' };
    const toolPath = `${this.toolsPath}/state_licensing_stats`;

    this.apiService.post(`${toolPath}`, payload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        if (!res) {
          this.stateLicensingStatsError.set('Unable to load state licensing stats');
          this.isLicensingLoading.set(false);
          return;
        }
        this.processStateLicensingStats(res);
        this.isLicensingLoading.set(false);
      },
      error: () => {
        this.stateLicensingStatsError.set('Unable to load state licensing stats');
        this.isLicensingLoading.set(false);
      }
    });
  }

  // API: { allowed_count, allowed_states_n, allowed_pct, not_allowed_count, not_allowed_states_n, not_allowed_pct }
  private processStateLicensingStats(response: any) {
    const resData = response?.data ?? response ?? {};

    const allowedCount = Number(resData.allowed_count) || 0;
    const notAllowedCount = Number(resData.not_allowed_count) || 0;
    const totalCount = allowedCount + notAllowedCount;

    const allowedPct = resData.allowed_pct !== undefined && resData.allowed_pct !== null
      ? Number(resData.allowed_pct)
      : (totalCount > 0 ? (allowedCount / totalCount) * 100 : 0);
    const notAllowedPct = resData.not_allowed_pct !== undefined && resData.not_allowed_pct !== null
      ? Number(resData.not_allowed_pct)
      : (totalCount > 0 ? (notAllowedCount / totalCount) * 100 : 0);

    this.stateLicensingStats.set({
      allowed_count: allowedCount,
      allowed_states_n: Number(resData.allowed_states_n) || 0,
      allowed_pct: isNaN(allowedPct) ? 0 : allowedPct,
      not_allowed_count: notAllowedCount,
      not_allowed_states_n: Number(resData.not_allowed_states_n) || 0,
      not_allowed_pct: isNaN(notAllowedPct) ? 0 : notAllowedPct
    });
  }

  private processOverallData(overall: any, overallPrev: any, runningTotal: any[]) {
    // Businesses Enrolled KPI
    const totalEnrolled = overall?.total_businesses || 0;
    const totalEnrolledPrev = overallPrev?.total_businesses || 0;

    let momEnrolled = 0;
    let momEnrolledText = "0.00%";
    if (totalEnrolledPrev > 0) {
      momEnrolled = ((totalEnrolled - totalEnrolledPrev) / totalEnrolledPrev) * 100;
      momEnrolledText = `${momEnrolled > 0 ? '+' : ''}${momEnrolled.toFixed(2)}%`;
    }

    let netNew = 0;
    if (runningTotal && runningTotal.length) {
      netNew = runningTotal.reduce((sum, r) => sum + (r.new_enrollments || 0), 0);
    }
    const monthsDiff = dayjs(this.end_date).diff(dayjs(this.start_date), 'month') || 1;
    const avgMo = netNew / monthsDiff;

    this.enrolledBusinessesSignal.set({
      value: totalEnrolled,
      netNew,
      avgMo,
      mom: momEnrolled,
      momText: momEnrolledText,
      startFormatted: dayjs(this.start_date).format('MMM'),
      endFormatted: dayjs(this.end_date).format('MMM')
    });

    // Businesses with a Contract KPI
    const businessesWithContracts = overall?.businesses_with_contracts || 0;
    const toConvert = Math.abs(totalEnrolled - businessesWithContracts);

    let goalPct = 0;
    if (overall?.activation_rate_pct !== undefined && overall?.activation_rate_pct !== null) {
      goalPct = overall.activation_rate_pct;
    } else {
      goalPct = Math.min(totalEnrolled > 0 ? (businessesWithContracts / totalEnrolled) * 100 : 0, 100);
    }

    this.businessesWithContractSignal.set({
      value: businessesWithContracts,
      toConvert,
      goalPct,
      totalContracts: Number(overall?.total_contracts) || 0,
      newSignupActivationPct: Number(overall?.activation_rate_new_signup_pct) || 0,
    });
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (unit) {
      case 'hour':
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
      case 'day':
        if (start.isSame(end, 'month')) {
          return `${start.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      case 'week':
        return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
      case 'month':
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      case 'quarter':
        return `Q${start.quarter()} ${start.format('YYYY')}`;
      case 'year':
        return `${start.format('YYYY')}`;
      default:
        return `${start.format('DD MMM YYYY')}`;
    }
  }

  // Formats a bucket's x-axis/tooltip label from its real API-provided start/end/unit_date,
  // matching the format used by Contract Status Trends: day -> "Sep 01", week -> "10 Jun - 14 Jun",
  // month -> "Jan 2026", quarter -> "Q1 2026". Uses the bucket's own dates rather than guessing
  // boundaries, so it stays correct even for a partial first/last bucket.
  private formatEnrollmentBucketLabel(unit: string, bucket: any): string {
    const start = dayjs(bucket?.start);
    const end = dayjs(bucket?.end);
    switch (unit) {
      case 'week': {
        const rangeStart = start.isValid() ? start : end;
        const rangeEnd = end.isValid() ? end : start;
        return `${rangeStart.format('DD MMM')} - ${rangeEnd.format('DD MMM')}`;
      }
      case 'month':
        return (start.isValid() ? start : end).format('MMM YYYY');
      case 'quarter': {
        const match = /^(\d{4})-Q([1-4])$/.exec(bucket?.unit_date || '');
        if (match) return `Q${match[2]} ${match[1]}`;
        const d = start.isValid() ? start : end;
        return d.isValid() ? `Q${Math.floor(d.month() / 3) + 1} ${d.format('YYYY')}` : (bucket?.unit_date || bucket?.start || '');
      }
      case 'hour':
        return (start.isValid() ? start : end).format('MMM DD, HH:mm');
      case 'day':
      default:
        return (start.isValid() ? start : end).format('MMM DD');
    }
  }

  // Trend chart — enrolled businesses (left axis, bars) vs. total contracts (right axis, line).
  // Both series come from their own dedicated endpoints but are fetched with the exact same
  // start_time/end_time/unit as the rest of the dashboard, so their buckets line up 1:1.
  private processRunningTotals(enrolledRaw: any, contractsRaw: any) {
    const enrolledList: any[] = Array.isArray(enrolledRaw) ? enrolledRaw : (Array.isArray(enrolledRaw?.data) ? enrolledRaw.data : []);
    const contractsList: any[] = Array.isArray(contractsRaw) ? contractsRaw : (Array.isArray(contractsRaw?.data) ? contractsRaw.data : []);

    if (!enrolledList.length) {
      this.chart1Data.set([]);
      return;
    }

    const bucketKey = (b: any) => b?.unit_date || b?.start;
    const contractsByDate = new Map<string, any>();
    contractsList.forEach((c: any) => contractsByDate.set(bucketKey(c), c));

    const unit = this.unit();
    const chartData: BarChartData[] = enrolledList.map((e: any) => {
      const match = contractsByDate.get(bucketKey(e)) || {};
      const label = this.formatEnrollmentBucketLabel(unit, e);
      return {
        start: label,
        end: label,
        enrolled: Number(e.running_total_enrollments) || 0,
        contracts: Number(match.running_total_contracts) || 0,
      };
    });
    this.chart1Data.set(chartData);
  }

  // Recuvery Demo — booking status: training_statuses[], sorted by count desc,
  // each bar's width is count / max(count) * 100 (no percentage recalculated server-side here).
  private processBookingStatuses(data: any) {
    const resData = data?.data ?? data;
    const statuses: any[] = Array.isArray(resData?.training_statuses) ? resData.training_statuses : [];

    this.bookingStatusTotal.set(Number(resData?.total_trainings) || 0);

    if (!statuses.length) {
      this.bookingStatusList.set([]);
      return;
    }

    const sorted = [...statuses].sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0));
    const max = Math.max(...sorted.map(s => Number(s.count) || 0), 1);

    this.bookingStatusList.set(sorted.map((s, i) => ({
      name: s.status_name || 'Unknown',
      count: Number(s.count) || 0,
      pct: (Number(s.count) || 0) / max * 100,
      color: RecuveryPipelinePortfolioComponent.BOOKING_STATUS_COLORS[i % RecuveryPipelinePortfolioComponent.BOOKING_STATUS_COLORS.length],
    })));
  }

  // API: { states: [{ code, name, accounts, proposals, contracts, recovered_amt }] }
  private processProposalConversionData(res: any) {
    if (!res) {
      this.stateOutreachData.set([]);
      this.proposalConversionChartData.set([]);
      this.stateOutreachTop5.set([]);
      this.stateOutreachError.set('Unable to load state mix data');
      return;
    }

    this.stateOutreachError.set('');

    const raw = res?.data ?? res;
    const states: any[] = Array.isArray(raw?.states) ? raw.states : [];

    const mapped = states.map((s: any) => ({
      code: s.code || '',
      name: s.name || s.code || 'Unknown',
      accounts: Number(s.accounts) || 0,
      proposals: Number(s.proposals) || 0,
      contracts: Number(s.contracts) || 0,
      recovered_amt: Number(s.recovered_amt) || 0
    }));

    this.stateOutreachData.set(mapped);

    if (!mapped.length) {
      this.proposalConversionChartData.set([]);
      this.stateOutreachTop5.set([]);
      return;
    }

    // Chart shows only the top 5 states by Accounts in Recovery (Proposals Sent as tiebreaker).
    const top5 = [...mapped]
      .sort((a, b) => (b.accounts - a.accounts) || (b.proposals - a.proposals))
      .slice(0, 5);
    this.stateOutreachTop5.set(top5);

    // Grouped bars: 2 consecutive entries per state (Proposals Sent, Accounts in Recovery),
    // rendered side-by-side under one shared label via [groupSize]="2" on the chart.
    const chartData: BarData[] = top5.flatMap(s => ([
      {
        label: s.name,
        tooltipLabel: s.name,
        tooltipValueLabel: `Proposals Sent: ${s.proposals.toLocaleString('en-US')}`,
        value: s.proposals,
        color: '#0d9488'
      },
      {
        label: s.name,
        tooltipLabel: s.name,
        tooltipValueLabel: `Accounts in Recovery: ${s.accounts.toLocaleString('en-US')}`,
        value: s.accounts,
        color: '#7c3aed'
      }
    ]));

    this.proposalConversionChartData.set(chartData);
  }

  // INDUSTRY DATA FETCHING

  // Industry Risk Quadrant — sub_industries_contracts, one call per vertical "type" (0/1/2)
  private fetchIndustryData() {
    this.isIndustryLoading.set(true);
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.datesReady) {
      this.isIndustryLoading.set(false);
      this.fetchTopCategoriesData();
      return;
    }
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const options = {
      showErrorToaster: false,
      showSuccessToaster: false,
      showLoader: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };
    const basePayload = {
      ...this.getPayloadData(),
      is_dummy: 0,
      offset: 0,
      productId,
    };
    const toolPath = `${this.toolsPath}/sub_industries_contracts`;

    const reqs = {
      type0: this.apiService.post(toolPath, { ...basePayload, type: 0 }, options).pipe(catchError(() => of(null))),
      type1: this.apiService.post(toolPath, { ...basePayload, type: 1 }, options).pipe(catchError(() => of(null))),
      type2: this.apiService.post(toolPath, { ...basePayload, type: 2 }, options).pipe(catchError(() => of(null))),
    };

    forkJoin(reqs).subscribe({
      next: (res: any) => {
        let allSpecialties: any[] = [];
        if (Array.isArray(res.type0?.data)) allSpecialties = allSpecialties.concat(res.type0.data);
        if (Array.isArray(res.type1?.data)) allSpecialties = allSpecialties.concat(res.type1.data);
        if (Array.isArray(res.type2?.data)) allSpecialties = allSpecialties.concat(res.type2.data);

        this.processRiskQuadrant(allSpecialties);
        this.isIndustryLoading.set(false);
      },
      error: () => this.isIndustryLoading.set(false)
    });

    this.fetchTopCategoriesData();
  }

  // Top Industry Categories — industry_contracts (flat data[] of { ind_name, total_contracts })
  private fetchTopCategoriesData() {
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.datesReady) return;
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const options = {
      showErrorToaster: false,
      showSuccessToaster: false,
      showLoader: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };
    const payload = {
      ...this.getPayloadData(),
      overall: 1,
      productId,
    };
    const toolPath = `${this.toolsPath}/industry_contracts`;

    this.apiService.post(`${toolPath}`, payload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        const list: any[] = Array.isArray(res?.data) ? res.data : [];
        this.processTopCategories(list);
      },
      error: () => { }
    });
  }

  private processTopCategories(data: any[]) {
    const sorted = [...data].sort((a, b) => (Number(b.total_contracts) || 0) - (Number(a.total_contracts) || 0));

    this.topIndustrySummary.set(sorted.slice(0, 2).map((item: any) => item.ind_name || 'Unknown'));

    const chartData = sorted.slice(0, 5).map((item: any) => {
      const fullLabel = item.ind_name || 'Unknown';
      const value = Number(item.total_contracts) || 0;
      return {
        start: fullLabel,
        end: fullLabel,
        data1: value,
        contracts: value,
        category: fullLabel,
        fullCategory: fullLabel
      };
    });
    this.topCategoriesData.set(chartData);
  }

  // Splits each axis at its own median — no fixed threshold logic existed for this quadrant
  // elsewhere in the codebase, so this stays a single, easy-to-adjust spot (swap `median`
  // for a percentile or a fixed cutoff here if the business definition changes).
  private median(values: number[]): number {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  private processRiskQuadrant(data: any[]) {
    const q1: any[] = []; // High profit, Low risk
    const q2: any[] = []; // High profit, High risk
    const q3: any[] = []; // Moderate, Med risk
    const q4: any[] = []; // High overdue risk

    const seenNames = new Set<string>();
    const unique: { name: string; profit: number; risk: number; contracts: number }[] = [];

    data.forEach((item: any) => {
      const name = item.speciality_name || 'Unknown';
      if (seenNames.has(name)) return;
      seenNames.add(name);
      unique.push({
        name,
        profit: Number(item.average_financed_amount) || 0,
        risk: Number(item.overdue_ratio) || 0,
        contracts: Number(item.total_contracts) || 0,
      });
    });

    if (!unique.length) {
      this.riskQuadrantData.set({ q1, q2, q3, q4 });
      return;
    }

    const profitThreshold = this.median(unique.map(u => u.profit));
    const riskThreshold = this.median(unique.map(u => u.risk));

    const sorted = [...unique].sort((a, b) => b.contracts - a.contracts);

    sorted.forEach((entry) => {
      if (entry.profit >= profitThreshold && entry.risk < riskThreshold) {
        if (q1.length < 3) q1.push(entry);
      } else if (entry.profit >= profitThreshold && entry.risk >= riskThreshold) {
        if (q2.length < 3) q2.push(entry);
      } else if (entry.profit < profitThreshold && entry.risk < riskThreshold) {
        if (q3.length < 3) q3.push(entry);
      } else {
        if (q4.length < 3) q4.push(entry);
      }
    });

    this.riskQuadrantData.set({ q1, q2, q3, q4 });
  }

  // RETENTION DATA FETCHING
  // New signals for flag data
  verificationBacklog = signal<any>(null);
  cancelledContractAttrition = signal<any>(null);
  growthOutpacingThroughput = signal<any>(null);
  decisionFlagsError = signal<string>('');

  // Verification Backlog Gate — computed straight from the Accounts Added stage's own
  // pending_verification/processed sub-metrics (journey_flow_debt), instead of trusting the
  // decision-flags API's finding_text string. Always reads journeyFlowDebt() directly (not
  // getJourneyStage()/activeJourneyFlow()) because 'accounts_added' only exists on the debt
  // flow, independent of which journey tab ('debt' vs 'contract') the user has selected
  // elsewhere on the page. Recomputes automatically whenever journeyFlowDebt() changes (date
  // range, product, etc.).
  verificationBacklogStats = computed<{ count: number; total: number; pct: number } | null>(() => {
    const stage = this.journeyFlowDebt()?.stages.find(s => s.key === 'accounts_added') ?? null;
    if (!stage) return null;

    const pending = Number(stage.sub_metrics?.['pending_verification']) || 0;
    const processed = Number(stage.sub_metrics?.['processed']) || 0;
    const total = pending + processed || Number(stage.value) || 0;
    const pct = total > 0 ? (pending / total) * 100 : 0;

    return { count: pending, total, pct };
  });

  verificationBacklogText = computed<string | null>(() => {
    const stats = this.verificationBacklogStats();
    if (!stats) return null;
    const countText = stats.count.toLocaleString('en-US');
    const pctText = stats.pct.toFixed(1);
    return `${countText} accounts (${pctText}%) are awaiting verification, a required step before Notifications can begin.`;
  });

  // Severity -> accent color mapping for Decision Flag cards (falls back to neutral slate).
  private readonly severityColors: Record<string, { border: string; cardBorder: string; dot: string; text: string }> = {
    critical: { border: '#DC2626', cardBorder: 'border-red-200 dark:border-red-900/40', dot: '#DC2626', text: '#DC2626' },
    high: { border: '#DC2626', cardBorder: 'border-red-200 dark:border-red-900/40', dot: '#DC2626', text: '#DC2626' },
    red: { border: '#DC2626', cardBorder: 'border-red-200 dark:border-red-900/40', dot: '#DC2626', text: '#DC2626' },
    medium: { border: '#F59E0B', cardBorder: 'border-amber-200 dark:border-amber-900/40', dot: '#F59E0B', text: '#D97706' },
    warning: { border: '#F59E0B', cardBorder: 'border-amber-200 dark:border-amber-900/40', dot: '#F59E0B', text: '#D97706' },
    amber: { border: '#F59E0B', cardBorder: 'border-amber-200 dark:border-amber-900/40', dot: '#F59E0B', text: '#D97706' },
    orange: { border: '#F59E0B', cardBorder: 'border-amber-200 dark:border-amber-900/40', dot: '#F59E0B', text: '#D97706' },
    yellow: { border: '#F59E0B', cardBorder: 'border-amber-200 dark:border-amber-900/40', dot: '#F59E0B', text: '#D97706' },
    low: { border: '#059669', cardBorder: 'border-emerald-200 dark:border-emerald-900/40', dot: '#059669', text: '#059669' },
    good: { border: '#059669', cardBorder: 'border-emerald-200 dark:border-emerald-900/40', dot: '#059669', text: '#059669' },
    green: { border: '#059669', cardBorder: 'border-emerald-200 dark:border-emerald-900/40', dot: '#059669', text: '#059669' },
  };

  getSeverityColors(severity: string | undefined | null) {
    return this.severityColors[(severity || '').toLowerCase()] ?? { border: '#94A3B8', cardBorder: 'border-slate-100 dark:border-slate-700', dot: '#94A3B8', text: '#64748B' };
  }

  formatFindingHtml(text: string | null | undefined): SafeHtml {
    if (!text) return '';
    // Finding text comes from the API (possibly model-written): escaped before any markup is added.
    const formatted = escapeHtml(text).replace(
      /((?:\+|-|\$)?\b\d+(?:,\d{3})*(?:\.\d+)?(?:%|pp|K|M|B)?)/g,
      '<strong class="font-[700] text-black dark:text-white">$1</strong>'
    );
    return this.sanitizer.bypassSecurityTrustHtml(formatted);
  }

  // Journey card trend helpers — direction/color derived from the real mom% sign, never hardcoded.
  trendArrow(pct: number | null | undefined): string {
    return (pct ?? 0) >= 0 ? '↑' : '↓';
  }

  trendColorClass(pct: number | null | undefined): string {
    return (pct ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-500';
  }

  absDelta(pct: number | null | undefined): number {
    return Math.abs(pct ?? 0);
  }

  formatCompactCurrency(value: number | null | undefined): string {
    const n = Number(value) || 0;
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  }

  private mapDecisionFlag(f: any) {
    return {
      category: f?.category ?? null,
      severity: f?.severity ?? null,
      metricValue: f?.metric_value,
      metricPct: f?.metric_pct,
      text: f?.finding_text,
      action: null as string | null,
    };
  }

  // The API stopped returning a "growth_outpacing_throughput" flag in flags[] and instead
  // returns raw growth percentages at the top level of the response:
  // business_registered_growth_pct, account_added_growth_pct, contract_created_growth_pct.
  // This derives the same decision (is input growth outpacing contract-creation throughput?)
  // directly from those real percentages — nothing here is hardcoded.
  private computeGrowthOutpacingFlag(raw: any): { category: string; severity: string; metricValue: undefined; metricPct: number; text: string; action: string } | null {
    const registeredGrowth = Number(raw?.business_registered_growth_pct);
    const accountGrowth = Number(raw?.account_added_growth_pct);
    const contractGrowth = Number(raw?.contract_created_growth_pct);

    const inputs: { label: string; value: number }[] = [];
    if (!Number.isNaN(registeredGrowth)) inputs.push({ label: 'Business registrations', value: registeredGrowth });
    if (!Number.isNaN(accountGrowth)) inputs.push({ label: 'Accounts added', value: accountGrowth });

    if (!inputs.length || Number.isNaN(contractGrowth)) return null;

    // Whichever input side is growing fastest is the one being compared against
    // contract-creation throughput.
    const leadingInput = inputs.reduce((max, m) => (m.value > max.value ? m : max), inputs[0]);
    const gapPct = leadingInput.value - contractGrowth;
    const fmt = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;

    let severity: string;
    let text: string;
    let action: string;

    if (gapPct > 20) {
      severity = 'critical';
      text = `${leadingInput.label} grew ${fmt(leadingInput.value)} while contracts created grew only ${fmt(contractGrowth)}, a ${gapPct.toFixed(1)}pp throughput gap.`;
      action = `Audit onboarding-to-contract conversion now: ${leadingInput.label.toLowerCase()} is growing far faster than contract throughput can absorb.`;
    } else if (gapPct > 5) {
      severity = 'medium';
      text = `${leadingInput.label} grew ${fmt(leadingInput.value)} vs. ${fmt(contractGrowth)} contract growth: throughput is falling behind.`;
      action = `Check onboarding conversion before adding acquisition spend: activation gap, or downstream capacity?`;
    } else {
      severity = 'good';
      text = `Contract creation growth (${fmt(contractGrowth)}) is keeping pace with ${leadingInput.label.toLowerCase()} (${fmt(leadingInput.value)}).`;
      action = `No action needed: throughput is keeping pace with input growth.`;
    }

    return {
      category: 'growth_outpacing_throughput',
      severity,
      metricValue: undefined,
      metricPct: Math.round(gapPct * 10) / 10,
      text,
      action,
    };
  }

  // RETENTION DATA FETCHING
  private fetchRetentionData() {
    this.isRetentionLoading.set(true);
    this.decisionFlagsError.set('');
    const productId = this.apiService.selectedProductId();
    if (productId && this.datesReady) {
      const productConfig = this.productConfigService.getProductConfig(
        this.productConfigService.getProductName(productId)
      );
      const options = {
        showErrorToaster: false,
        showSuccessToaster: false,
        showLoader: false,
        cache: true,
        addAccessToken: productConfig.requiresAccessToken,
        productServerName: productConfig.productServerName,
      };
      const payload = {
        ...this.getPayloadData(),
        country_id: 1,
        productId,
        top_n: 6,
      };
      const toolPath = `${this.toolsPath}/pipeline_decision_flags`;

      this.apiService.post(`${toolPath}`, payload, options).pipe(
        catchError(() => of(null))
      ).subscribe({
        next: (res: any) => {
          if (!res) {
            this.verificationBacklog.set(null);
            this.cancelledContractAttrition.set(null);
            this.growthOutpacingThroughput.set(null);
            this.decisionFlagsError.set('Unable to load decision flags');
            this.isRetentionLoading.set(false);
            return;
          }

          const raw = res?.data ?? res;
          const flags: any[] = Array.isArray(raw?.flags) ? raw.flags : [];
          this.verificationBacklog.set(null);
          this.cancelledContractAttrition.set(null);

          let growthFlag: any = null;
          flags.forEach((f: any) => {
            if (f.category === 'verification_backlog') {
              this.verificationBacklog.set(this.mapDecisionFlag(f));
            } else if (f.category === 'cancelled_contract_attrition') {
              this.cancelledContractAttrition.set(this.mapDecisionFlag(f));
            } else if (f.category === 'growth_outpacing_throughput') {
              growthFlag = f;
            }
          });

          // Prefer a real flag from the API if one is present; otherwise derive the same
          // decision from the growth percentages the response now carries at the top level.
          this.growthOutpacingThroughput.set(
            growthFlag ? this.mapDecisionFlag(growthFlag) : this.computeGrowthOutpacingFlag(raw)
          );

          this.isRetentionLoading.set(false);
        },
        error: () => {
          this.verificationBacklog.set(null);
          this.cancelledContractAttrition.set(null);
          this.growthOutpacingThroughput.set(null);
          this.decisionFlagsError.set('Unable to load decision flags');
          this.isRetentionLoading.set(false);
        }
      });

    } else {
      this.isRetentionLoading.set(false);
    }
  }

  // PLATFORM JOURNEY — Debt Recovery / Contract Lifecycle stage flow
  private fetchJourneyFlows() {
    this.isJourneyLoading.set(true);
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.datesReady) {
      this.isJourneyLoading.set(false);
      return;
    }
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const options = {
      showErrorToaster: false,
      showSuccessToaster: false,
      showLoader: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };
    const payload = {
      ...this.getPayloadData(),
      productId,
    };
    const toolPath = this.toolsPath;

    forkJoin({
      debt: this.apiService.post(`${toolPath}/journey_flow_debt`, payload, options).pipe(catchError(() => of(null))),
      contract: this.apiService.post(`${toolPath}/journey_flow_contract`, payload, options).pipe(catchError(() => of(null))),
    }).subscribe({
      next: (results: any) => {
        this.journeyFlowDebt.set(this.mapJourneyFlowResponse(results.debt));
        this.journeyFlowContract.set(this.mapJourneyFlowResponse(results.contract));
        this.isJourneyLoading.set(false);
      },
      error: () => {
        this.isJourneyLoading.set(false);
      }
    });
  }

  private mapJourneyFlowResponse(res: any): JourneyFlowResponse | null {
    const data = res?.data ?? res;
    if (!data) return null;

    const rootRaw = data.root || {};
    const rootValue = Number(rootRaw.value) || 0;
    const root: JourneyFlowRoot = {
      key: rootRaw.key,
      name: rootRaw.name,
      sub_label: rootRaw.sub_label,
      value: rootValue,
      delta_pct: Number(rootRaw.delta_pct) || 0,
      sub_metrics: rootRaw.sub_metrics || {},
    };

    const rawStages: any[] = Array.isArray(data.stages) ? data.stages : [];
    const stages: JourneyStage[] = rawStages.map((s: any, idx: number) => {
      const value = Number(s.value) || 0;
      // Fallback only — the API already returns pct_of_prev directly in the common case.
      const prevValue = idx > 0 ? Number(rawStages[idx - 1]?.value) || 0 : rootValue;
      const pctOfPrev = (s.pct_of_prev !== undefined && s.pct_of_prev !== null)
        ? Number(s.pct_of_prev)
        : (prevValue > 0 ? (value / prevValue) * 100 : 0);

      return {
        key: s.key,
        name: s.name,
        value,
        delta_pct: Number(s.delta_pct) || 0,
        pct_of_prev: pctOfPrev,
        sub_metrics: s.sub_metrics || {},
      };
    });

    // Fallback only — journey_flow_contract already returns contracts_per_business directly.
    let contractsPerBusiness = data.contracts_per_business;
    if (contractsPerBusiness === undefined || contractsPerBusiness === null) {
      const contractStage = stages.find(s => s.key === 'contract_created');
      const businessesCovered = Number(contractStage?.sub_metrics?.['businesses_covered']) || 0;
      contractsPerBusiness = businessesCovered > 0 ? (contractStage!.value / businessesCovered) : 0;
    }

    return { root, stages, contracts_per_business: Number(contractsPerBusiness) || 0 };
  }

  // RECOVERY CONVERSION & VALUE LEAK FUNNEL
  private fetchRecoveryConversionFunnel() {
    this.isConversionFunnelLoading.set(true);
    const productId = this.apiService.selectedProductId();
    if (!productId || !this.datesReady) {
      this.isConversionFunnelLoading.set(false);
      return;
    }
    const productConfig = this.productConfigService.getProductConfig(
      this.productConfigService.getProductName(productId)
    );
    const options = {
      showErrorToaster: false,
      showSuccessToaster: false,
      showLoader: false,
      cache: true,
      addAccessToken: productConfig.requiresAccessToken,
      productServerName: productConfig.productServerName,
    };
    const payload = {
      ...this.getPayloadData(),
      productId,
    };
    const toolPath = `${this.toolsPath}/recovery_conversion_funnel`;

    this.apiService.post(`${toolPath}`, payload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        const rawSteps: any[] = Array.isArray(res?.data?.steps) ? res.data.steps : [];
        this.recoveryConversionFunnel.set(rawSteps.map((s: any): RecoveryFunnelStep => ({
          key: s.key,
          name: s.name,
          count: Number(s.count) || 0,
          amount: Number(s.amount) || 0,
          pct_of_total: Number(s.pct_of_total) || 0,
          leak_count: Number(s.leak_count) || 0,
          leak_amount: Number(s.leak_amount) || 0,
          leak_note: s.leak_note ?? null,
        })));
        this.isConversionFunnelLoading.set(false);
      },
      error: () => {
        this.isConversionFunnelLoading.set(false);
      }
    });
  }

  // CONTRACT STATE SUMMARY TABLE
  private fetchContractStateSummary() {
    this.isTableLoading.set(true);
    this.contractStateSummaryError.set('');
    const productId = this.apiService.selectedProductId();
    const payload = { ...this.getPayloadData(), productId };
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'recuvery' };
    const toolPath = `${this.toolsPath}/account_state_summary`;
    this.apiService.post(`${toolPath}`, payload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        if (!res) {
          this.contractStateSummary.set([]);
          this.contractStateSummaryError.set('Unable to load account state summary');
          this.isTableLoading.set(false);
          return;
        }
        this.processContractStatesBreakdown(res);
        this.isTableLoading.set(false);
      },
      error: () => {
        this.contractStateSummary.set([]);
        this.contractStateSummaryError.set('Unable to load account state summary');
        this.isTableLoading.set(false);
      }
    });
  }

  private stateSignalBadge(signalRaw: string | undefined, fallbackLabel: string): { text: string; colorClass: string } {
    const s = String(signalRaw || fallbackLabel || '').toLowerCase().replace(/_/g, ' ').trim();
    if (s === 'good' || s === 'verified' || s === 'healthy' || s === 'active' || s.includes('verif')) {
      return { text: '✓ Healthy', colorClass: 'text-[#15803d]' };
    }
    if (s === 'bad' || s === 'non verified' || s === 'unverified' || s === 'nonverified' || s === 'risk' || s.includes('non verif')) {
      return { text: '⚠ Risk', colorClass: 'text-[#b91c1c]' };
    }
    if (s === 'mute' || s === 'clear') {
      return { text: '✓ Clear', colorClass: 'text-[#15803d]' };
    }
    if (s === 'on hold' || s === 'pause payment' || s === 'paused' || s === 'pause' || s.includes('cash flow') || s.includes('payment')) {
      return { text: '⚠ On hold', colorClass: 'text-[#b45309]' };
    }
    if (s === 'warn' || s === 'dnd' || s.includes('disengaged') || s.includes('disturb') || s === 'do not disturb') {
      return { text: '⚠ Disengaged', colorClass: 'text-[#b45309]' };
    }
    if (s === 'disputed' || s.includes('dispute') || s.includes('immaterial')) {
      return { text: 'Immaterial', colorClass: 'text-[#6a7c93]' };
    }
    if (s === 'overdue' || s === 'late' || s === 'defaulted' || s.includes('overdue')) {
      return { text: '⚠ Overdue Risk', colorClass: 'text-[#b91c1c]' };
    }
    if (s === 'completed' || s === 'paid off' || s === 'paid') {
      return { text: '✓ Completed', colorClass: 'text-[#15803d]' };
    }
    if (s === 'cancelled' || s === 'canceled' || s === 'churn') {
      return { text: '⚠ Cancelled', colorClass: 'text-[#6a7c93]' };
    }
    return { text: '• Normal', colorClass: 'text-slate-600 dark:text-slate-400' };
  }

  private formatStateLabel(name: string): string {
    const raw = String(name || '').trim();
    if (raw.toLowerCase() === 'dnd') return 'DND';
    if (raw.toLowerCase() === 'non_verified' || raw.toLowerCase() === 'nonverified') return 'Non Verified';
    if (raw.toLowerCase() === 'pause_payment' || raw.toLowerCase() === 'pausepayment') return 'Pause Payment';
    return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  // API: { total_book, states: [{ label, count, pct_of_book, signal }] }
  private processContractStatesBreakdown(response: any) {
    const resData = response?.data ?? response ?? {};
    const totalBook = Number(resData.total_book) || 0;
    const states: any[] = Array.isArray(resData.states) ? resData.states : [];

    this.contractStateHeadline.set(totalBook ? String(totalBook) : '');

    if (!states.length) {
      this.contractStateSummary.set([]);
      this.disengagedAccounts.set({ dndCount: 0, dndPct: 0 });
      return;
    }

    const summary = states.map((r: any) => {
      const count = Number(r.count) || 0;
      const rawLabel = r.label || r.state || r.name || 'Unknown';
      const pct = r.pct_of_book !== undefined && r.pct_of_book !== null
        ? Number(r.pct_of_book)
        : (totalBook > 0 ? (count / totalBook) * 100 : 0);
      return {
        state: this.formatStateLabel(rawLabel),
        count,
        pct: pct || 0,
        signal: this.stateSignalBadge(r.signal, rawLabel)
      };
    });

    this.contractStateSummary.set(summary);

    const dndItem = states.find((item: any) => {
      const label = String(item?.label || item?.state || item?.name || '').toLowerCase().trim();
      return label === 'dnd' || label.includes('disengaged') || label === 'do not disturb';
    });
    if (dndItem) {
      const dndCount = Number(dndItem.count) || 0;
      const dndPct = dndItem.pct_of_book !== undefined && dndItem.pct_of_book !== null
        ? Number(dndItem.pct_of_book)
        : (totalBook > 0 ? (dndCount / totalBook) * 100 : 0);
      this.disengagedAccounts.set({ dndCount, dndPct: isNaN(dndPct) ? 0 : Number(dndPct.toFixed(1)) });
    } else {
      this.disengagedAccounts.set({ dndCount: 0, dndPct: 0 });
    }
  }
  // CONTRACT STATUS TRENDS CHART — one bucket per unit (day/week/month/quarter), driven by
  // the shared date-range/group-by selector (this.unit()), not a hardcoded granularity.
  //
  // Verified response shape (curled live for day/week/month/quarter units):
  //   { data: { unit, unit_data: [{ unit_date, as_of, total, active, overdue, completed, cancelled }], latest } }
  // `as_of` is always a real, parseable date (the bucket's end date / snapshot date) for every
  // unit; `unit_date` is only a real date for 'day' and 'month' — for 'week' it's an ISO
  // year-week ("2026-30") and for 'quarter' it's "2025-Q3", so bucket boundaries for the
  // week label are derived from sequential `as_of` values instead of parsing `unit_date`.
  private fetchContractStatusTrends() {
    this.isChartLoading.set(true);
    this.contractStatusTrendsError.set('');
    // Clear immediately so a stale chart from the previous date range/unit never lingers
    // while the new request is in flight.
    this.contractStatusTrends.set([]);

    const productId = this.apiService.selectedProductId();
    const unit = this.unit();
    const payload = { ...this.getPayloadData(), unit, productId };
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'recuvery' };
    const toolPath = `${this.toolsPath}/contract_status_trend`;

    this.apiService.post(`${toolPath}`, payload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        if (!res) {
          this.contractStatusTrends.set([]);
          this.contractStatusTrendsError.set('Unable to load contract status trends');
          this.isChartLoading.set(false);
          return;
        }

        const raw = res?.data ?? res;
        const data: any[] = Array.isArray(raw?.unit_data) ? raw.unit_data : [];
        const responseUnit = raw?.unit || unit;

        if (!data.length) {
          this.contractStatusTrends.set([]);
          this.isChartLoading.set(false);
          return;
        }

        const rangeStart = dayjs(this.start_date);
        const chartData: StackedBarItem[] = data.map((d: any, i: number) => {
          const prevAsOf = i > 0 ? data[i - 1]?.as_of : null;
          return {
            label: this.formatTrendBucketLabel(responseUnit, d, prevAsOf, rangeStart),
            active: Number(d.active) || 0,
            overdue: Number(d.overdue) || 0,
            completed: Number(d.completed) || 0,
            cancelled: Number(d.cancelled) || 0
          };
        });

        this.contractStatusTrends.set(chartData);

        // Headline shows the latest bucket's values from unit_data[] (currently unused in
        // the template, kept for parity with the chart data).
        const latest = raw?.latest || data[data.length - 1] || {};
        this.contractStatusCounts.set({
          month: this.formatTrendBucketLabel(responseUnit, latest, data.length > 1 ? data[data.length - 2]?.as_of : null, rangeStart),
          active: Number(latest.active) || 0,
          overdue: Number(latest.overdue) || 0,
          completed: Number(latest.completed) || 0,
          cancelled: Number(latest.cancelled) || 0
        });

        this.isChartLoading.set(false);
      },
      error: () => {
        this.contractStatusTrends.set([]);
        this.contractStatusTrendsError.set('Unable to load contract status trends');
        this.isChartLoading.set(false);
      }
    });
  }

  // Formats one trend bucket's x-axis label per the active granularity:
  //   day     -> "Sep 01"            (unit_date is already a real date)
  //   week    -> "20 Jul - 26 Jul"   (start derived from the previous bucket's as_of + 1 day,
  //                                   or the selected range start for the first bucket)
  //   month   -> "Feb 2026"          (unit_date is the first-of-month, already a real date)
  //   quarter -> "Q3 2025"           (unit_date is "2025-Q3"; just reordered)
  private formatTrendBucketLabel(unit: string, bucket: any, prevAsOf: string | null, rangeStart: dayjs.Dayjs): string {
    const asOf = dayjs(bucket?.as_of);

    switch (unit) {
      case 'day':
        return (dayjs(bucket?.unit_date).isValid() ? dayjs(bucket.unit_date) : asOf).format('MMM DD');
      case 'week': {
        const bucketEnd = asOf.isValid() ? asOf : dayjs(bucket?.unit_date);
        const bucketStart = prevAsOf && dayjs(prevAsOf).isValid() ? dayjs(prevAsOf).add(1, 'day') : rangeStart;
        return `${bucketStart.format('DD MMM')} - ${bucketEnd.format('DD MMM')}`;
      }
      case 'month':
        return (dayjs(bucket?.unit_date).isValid() ? dayjs(bucket.unit_date) : asOf).format('MMM YYYY');
      case 'quarter': {
        const match = /^(\d{4})-Q([1-4])$/.exec(bucket?.unit_date || '');
        if (match) return `Q${match[2]} ${match[1]}`;
        return asOf.isValid() ? `Q${asOf.quarter()} ${asOf.format('YYYY')}` : (bucket?.unit_date || '');
      }
      default:
        return bucket?.unit_date || (asOf.isValid() ? asOf.format('MMM DD, YYYY') : '');
    }
  }


  // API: { states: [{ code, name, accounts, paid, recovered_amt }] }
  // Ranking/aggregation into top-5 + "Other States" happens in the stateRollupDisplay
  // computed, against the chart's own top-5 (stateOutreachTop5) for consistency.
  private fetchRevenueStreamMix() {
    this.isRevenueLoading.set(true);
    this.stateRollupError.set('');
    const productId = this.apiService.selectedProductId();
    const payload = { ...this.getPayloadData(), productId };
    const options = { showLoader: false, showErrorToaster: false, productServerName: this.productConfig()?.productServerName || 'recuvery' };
    const toolPath = `${this.toolsPath}/state_rollup`;

    this.apiService.post(`${toolPath}`, payload, options).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (res: any) => {
        if (!res) {
          this.stateRollupRaw.set([]);
          this.stateRollupError.set('Unable to load state rollup');
          this.isRevenueLoading.set(false);
          return;
        }

        const raw = res?.data ?? res;
        const states: any[] = Array.isArray(raw?.states) ? raw.states : [];

        const mapped = states.map((s: any) => ({
          code: s.code || '',
          name: s.name || s.code || 'Unknown',
          accounts: Number(s.accounts) || 0,
          paid: Number(s.paid) || 0,
          recovered_amt: Number(s.recovered_amt) || 0
        }));

        this.stateRollupRaw.set(mapped);
        this.isRevenueLoading.set(false);
      },
      error: () => {
        this.stateRollupRaw.set([]);
        this.stateRollupError.set('Unable to load state rollup');
        this.isRevenueLoading.set(false);
      }
    });
  }
  processContractTypesCount(response: any) {
    const data = response?.unit_data || response?.data?.unit_data || response?.data || response;

    if (!data || !Array.isArray(data) || data.length === 0) {
      this.contractTypesCountData.set({ total: 0, highestCount: 0, highestLabel: '', highestPercentage: 0 });
      return;
    }

    const total = data.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    let highestCount = 0;
    let highestLabel = '';

    data.forEach(item => {
      const count = Number(item.count) || 0;
      if (count > highestCount) {
        highestCount = count;
        highestLabel = item.contract_type_label || item.contract_type_name || '';
      }
    });

    const highestPercentage = total > 0 ? Math.round((highestCount / total) * 100) : 0;

    this.contractTypesCountData.set({
      total,
      highestCount,
      highestLabel,
      highestPercentage
    });
  }
}

